import {useCallback, useEffect, useRef, useState} from 'react'
import {announce} from '@github-ui/aria-live'
import {Button, Link, Stack} from '@primer/react'
import {AgentIcon} from '@primer/octicons-react'
import {useMutation} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useAgenticAutofixJobPoll} from '@github-ui/code-scanning-shared/hooks/use-agentic-autofix-job-poll'
import {
  AgenticAutofixError,
  handleAgenticAutofixErrorResponse,
} from '@github-ui/code-scanning-shared/types/agentic-autofix-error'
import {displayFlashMessage} from '@github-ui/code-scanning-shared/utils/display-flash-message'
import {useWorkflowApproval} from './use-workflow-approval'

export type AgenticAutofixInitialState = 'unassigned' | 'pending' | 'completed' | 'failed'

export interface AssignToCopilotProps {
  url: string
  alertNumber: number
  commentId: number
  repository: {owner: string; name: string}
  initialState?: AgenticAutofixInitialState
  agenticAutofixJobId?: string
  sessionUrl?: string
}

interface AssignToCopilotButtonProps extends AssignToCopilotProps {
  basePageDataUrl: string
}

type AssignToCopilotResponse = {agenticAutofixJobId?: string}

function repoSessionPath(repository: {owner: string; name: string}, sessionId: string): string {
  return `/${encodeURIComponent(repository.owner)}/${encodeURIComponent(repository.name)}/sessions/${encodeURIComponent(sessionId)}`
}

export function AssignToCopilotButton({
  basePageDataUrl,
  url,
  alertNumber,
  commentId,
  repository,
  initialState,
  agenticAutofixJobId,
  sessionUrl,
}: AssignToCopilotButtonProps) {
  const [jobId, setJobId] = useState<string | null>(initialState === 'pending' ? (agenticAutofixJobId ?? null) : null)
  const completionHandledRef = useRef(false)

  const handleJobError = useCallback((message: string) => {
    displayFlashMessage('error', `Copilot failed to create a pull request. ${message}.`)
    setJobId(null)
  }, [])

  const {isComplete: polledJobIsComplete, sessionId} = useAgenticAutofixJobPoll(
    repository.owner,
    repository.name,
    jobId,
    handleJobError,
    {
      completeOnPullRequest: false,
    },
  )

  const {mutate, isPending} = useMutation({
    mutationFn: async (): Promise<AssignToCopilotResponse> => {
      const response = await reactFetchJSON(url, {
        method: 'POST',
        body: {alert_number: alertNumber, comment_id: commentId},
      })
      if (!response.ok) {
        await handleAgenticAutofixErrorResponse(response)
      }
      return (await response.json()) as AssignToCopilotResponse
    },
    onSuccess: data => {
      if (data.agenticAutofixJobId) {
        setJobId(data.agenticAutofixJobId)
      }
    },
    onError: (error: Error) => {
      const message = error instanceof AgenticAutofixError ? error.message : 'Something went wrong, please try again.'
      displayFlashMessage('error', message)
    },
  })

  const isComplete = initialState === 'completed' || polledJobIsComplete
  const isWorking = isPending || (jobId !== null && !isComplete)
  const hasAssociatedJob = agenticAutofixJobId !== undefined || jobId !== null
  const workflowApproval = useWorkflowApproval({
    basePageDataUrl,
    enabled: isComplete && hasAssociatedJob,
  })
  const label = isComplete ? 'Copilot session is done' : isWorking ? 'Copilot is working' : 'Assign to Copilot'
  const polledSessionHref = sessionId ? repoSessionPath(repository, sessionId) : undefined
  // `sessionUrl` only describes the job the page loaded with, so it is dropped once a job is polled or re-assigned.
  const restoredSessionHref = isPending || jobId !== null ? undefined : sessionUrl
  const sessionHref = polledSessionHref ?? restoredSessionHref
  const showSessionLink = Boolean(sessionHref) && (isWorking || isComplete)
  const showApproveWorkflows = isComplete && workflowApproval.canApprove

  useEffect(() => {
    if (!polledJobIsComplete || completionHandledRef.current) return

    completionHandledRef.current = true
    announce('Copilot session is done')
  }, [polledJobIsComplete])

  return (
    <Stack direction="horizontal" gap="condensed" align="center" justify="end" wrap="wrap">
      {showApproveWorkflows ? (
        <Button
          type="button"
          size="small"
          loading={workflowApproval.isApproving}
          loadingAnnouncement="Approving workflows"
          onClick={workflowApproval.approve}
          data-testid="approve-workflows-button"
        >
          Approve workflows to run
        </Button>
      ) : (
        <Button
          type="button"
          size="small"
          leadingVisual={AgentIcon}
          loading={isWorking}
          loadingAnnouncement="Assigning to Copilot"
          aria-disabled={isWorking || isComplete}
          inactive={isWorking || isComplete}
          onClick={isWorking || isComplete ? undefined : () => mutate()}
          data-testid="assign-to-copilot-button"
        >
          {label}
        </Button>
      )}
      {showSessionLink && (
        <Link href={sessionHref} className="text-small Link--inTextBlock">
          View agent session
        </Link>
      )}
      {workflowApproval.message && <span>{workflowApproval.message}</span>}
    </Stack>
  )
}
